import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  commonUtils,
  IActivityConfig,
  useNotifications,
  useEditorStore,
  useFlowBuilder2UpdateFlowStepMutation,
} from '@diana/core';
import { FLOW_BUILDER2_PAGE_NS } from '../i18n';

export const useUpdateActivity = (
  options?: Parameters<typeof useFlowBuilder2UpdateFlowStepMutation>[0],
) => {
  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);

  const notifications = useNotifications();

  const getFlow = useEditorStore((s) => s.getFlow);
  const updateFlow = useEditorStore((s) => s.updateFlow);

  const { mutateAsync, isPending: isUpdatingActivity } = useFlowBuilder2UpdateFlowStepMutation({
    ...options,
    onSuccess: (data, variables, context) => {
      updateFlow((draft) => {
        draft.steps = data;
        return draft;
      });
      options?.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      notifications.error({
        message: t('errors.addActivityFailed'),
        description: commonUtils.getErrorMessage(error),
      });
      options?.onError?.(error, variables, context);
    },
  });

  const updateActivity = useCallback(
    async (args: {
      actionSetId: string;
      actionId: string;
      activityId: string;
      data: IActivityConfig;
    }) => {
      const flow = getFlow();
      if (flow) {
        const actionSet = flow.steps.find((step) => step.actionSetId === args.actionSetId);

        if (actionSet) {
          return mutateAsync({
            flowId: flow.id,
            flowStepId: actionSet.id,
            original: actionSet,
            data: {
              id: actionSet.id,
              actions: actionSet.actions.map((action) => {
                if (action.id === args.actionId && action.activities) {
                  action.activities = action.activities.map((activity) => {
                    return activity.id === args.activityId ? args.data : activity;
                  });
                }
                return action;
              }),
            },
          });
        }
      }
    },
    [getFlow, mutateAsync],
  );

  return { updateActivity, isUpdatingActivity };
};
